/**
 * The single HTTP entry point.
 *
 * The access token is held in memory only — never in localStorage, which any injected script can
 * read. Losing it on a page refresh is fine: the httpOnly refresh cookie silently restores the
 * session on boot.
 */

let accessToken: string | null = null
let onUnauthenticated: (() => void) | null = null

export function setAccessToken(token: string | null) {
  accessToken = token
}

export function setUnauthenticatedHandler(handler: () => void) {
  onUnauthenticated = handler
}

/**
 * A failure the API described in RFC 7807 form, carrying a stable machine-readable code.
 *
 * Fields are declared and assigned explicitly rather than as constructor parameter properties —
 * the project builds with `erasableSyntaxOnly`, which rules out syntax that emits runtime code.
 */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: string[]
  readonly correlationId?: string

  constructor(
    status: number,
    code: string,
    message: string,
    details: string[] = [],
    correlationId?: string,
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
    this.correlationId = correlationId
  }
}

interface RequestOptions {
  method?: string
  body?: unknown
  /** Skips the refresh-and-retry dance. Used by the refresh call itself, to avoid a loop. */
  noRetry?: boolean
  signal?: AbortSignal
}

/**
 * One in-flight refresh at a time. Without this, five widgets loading at once each fire their own
 * refresh, and rotation means four of them present an already-used token.
 *
 * This is per-JS-context and cannot be anything else: a second tab, or a reload mid-flight, has its
 * own module instance and its own promise, while the refresh cookie belongs to the browser and is
 * shared by all of them. That gap used to sign the user out — the second request presented a token
 * the first had already rotated, and the server could not tell it from a replay.
 *
 * It is closed on the server now, by `Jwt:RefreshReuseWindowSeconds`: a duplicate arriving moments
 * after a rotation is answered with a fresh access token and no new cookie. Deliberately not a
 * `BroadcastChannel` or `localStorage` mutex here — a cross-tab lock still cannot help the reload
 * case, because the context holding the lock is gone and has no way to hand the new token over.
 */
let refreshInFlight: Promise<boolean> | null = null

async function refreshOnce(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
      })

      if (!response.ok) return false

      const result = (await response.json()) as { accessToken: string }
      accessToken = result.accessToken
      return true
    } catch {
      return false
    } finally {
      // Cleared on the next tick so callers awaiting this promise all see the same result.
      setTimeout(() => {
        refreshInFlight = null
      }, 0)
    }
  })()

  return refreshInFlight
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, noRetry = false, signal } = options

  const send = () =>
    fetch(path.startsWith('/api') ? path : `/api${path}`, {
      method,
      headers: {
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      credentials: 'include',
      signal,
    })

  let response = await send()

  // An expired access token is the ordinary case, not an error — refresh and try once more.
  if (response.status === 401 && !noRetry) {
    const refreshed = await refreshOnce()
    if (refreshed) {
      response = await send()
    } else {
      onUnauthenticated?.()
      throw new ApiError(401, 'UNAUTHENTICATED', 'Your session has ended. Sign in again.')
    }
  }

  if (!response.ok) {
    throw await toApiError(response)
  }

  if (response.status === 204) {
    return undefined as T
  }

  return (await response.json()) as T
}

/**
 * Multipart upload, with the same auth and refresh handling as `api`.
 *
 * Separate from `api` because the body is a `FormData` rather than JSON: the browser has to set
 * `Content-Type` itself so it can put the multipart boundary in the header, and setting it by hand
 * produces a request the server cannot parse.
 */
export async function upload<T>(path: string, form: FormData): Promise<T> {
  const url = path.startsWith('/api') ? path : `/api${path}`

  const send = () =>
    fetch(url, {
      method: 'POST',
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      body: form,
      credentials: 'include',
    })

  let response = await send()

  if (response.status === 401) {
    if (await refreshOnce()) {
      response = await send()
    } else {
      onUnauthenticated?.()
      throw new ApiError(401, 'UNAUTHENTICATED', 'Your session has ended. Sign in again.')
    }
  }

  if (!response.ok) {
    throw await toApiError(response)
  }

  return (await response.json()) as T
}

/** Downloads a file (an Excel export) with the same auth and refresh handling. */
export async function download(path: string, fallbackName: string): Promise<void> {
  const url = path.startsWith('/api') ? path : `/api${path}`

  const send = () =>
    fetch(url, {
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      credentials: 'include',
    })

  let response = await send()

  if (response.status === 401) {
    if (await refreshOnce()) {
      response = await send()
    } else {
      onUnauthenticated?.()
      throw new ApiError(401, 'UNAUTHENTICATED', 'Your session has ended. Sign in again.')
    }
  }

  if (!response.ok) {
    throw await toApiError(response)
  }

  // Prefer the filename the server chose — it carries the period, which matters for a report
  // someone will keep on disk for years.
  const disposition = response.headers.get('Content-Disposition') ?? ''
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)
  const name = match ? decodeURIComponent(match[1]) : fallbackName

  const blob = await response.blob()
  const objectUrl = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = objectUrl
  link.download = name
  document.body.appendChild(link)
  link.click()
  link.remove()

  URL.revokeObjectURL(objectUrl)
}

async function toApiError(response: Response): Promise<ApiError> {
  try {
    const problem = (await response.json()) as {
      title?: string
      detail?: string
      code?: string
      details?: string[]
      correlationId?: string
    }

    return new ApiError(
      response.status,
      problem.code ?? 'ERROR',
      problem.detail || problem.title || response.statusText,
      problem.details ?? [],
      problem.correlationId,
    )
  } catch {
    return new ApiError(response.status, 'ERROR', response.statusText || 'Request failed.')
  }
}

/** Builds a query string, dropping empty values so the URL stays readable. */
export function query(params: Record<string, string | number | boolean | null | undefined>): string {
  const search = new URLSearchParams()

  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== '') {
      search.set(key, String(value))
    }
  }

  const text = search.toString()
  return text ? `?${text}` : ''
}
